import React, {
	createContext,
	useContext,
	useImperativeHandle,
	useMemo,
} from 'react';
import {observer} from 'mobx-react-lite';
import {
	useSingleFileUpload,
	type UseSingleFileUploadParams,
	type UseSingleFileUploadType,
} from '../hooks';
import {UppyStateStore} from '../store';
import {UppyFileMeta} from '../types';

export type SingleFileUploadContextType<
	TMeta extends UppyFileMeta = UppyFileMeta,
	TStore extends UppyStateStore<TMeta> = UppyStateStore<TMeta>,
> = UseSingleFileUploadType<TMeta, TStore>;

export const SingleFileUploadContext = createContext<
	SingleFileUploadContextType | undefined
>(undefined);

export const useSingleFileUploadProvider = <
	TMeta extends UppyFileMeta = UppyFileMeta,
	TStore extends UppyStateStore<TMeta> = UppyStateStore<TMeta>,
>() => {
	const context = useContext(
		SingleFileUploadContext as React.Context<
			SingleFileUploadContextType<TMeta, TStore> | undefined
		>,
	);
	if (context === undefined) {
		throw new Error(
			'`useSingleFileUploadProvider` must be used within a `SingleFileUploadProvider`',
		);
	}
	return context;
};

export type SingleFileUploadProviderRef<
	TMeta extends UppyFileMeta = UppyFileMeta,
	TStore extends UppyStateStore<TMeta> = UppyStateStore<TMeta>,
> =
	| React.RefObject<SingleFileUploadContextType<TMeta, TStore>>
	| ((node: SingleFileUploadContextType<TMeta, TStore>) => void)
	| null;

export type SingleFileUploadProviderChildrenProps<
	TMeta extends UppyFileMeta = UppyFileMeta,
	TStore extends UppyStateStore<TMeta> = UppyStateStore<TMeta>,
> = SingleFileUploadContextType<TMeta, TStore>;

export type SingleFileUploadProviderProps<
	TMeta extends UppyFileMeta = UppyFileMeta,
	TStore extends UppyStateStore<TMeta> = UppyStateStore<TMeta>,
> = UseSingleFileUploadParams<TMeta, TStore> & {
	ref?: SingleFileUploadProviderRef;
	children:
		| React.ReactNode
		| ((
				props: SingleFileUploadProviderChildrenProps<TMeta, TStore>,
		  ) => React.ReactNode);
};

const SingleFileUploadProviderInner = <
	TMeta extends UppyFileMeta = UppyFileMeta,
	TStore extends UppyStateStore<TMeta> = UppyStateStore<TMeta>,
>(
	{children, ...props}: SingleFileUploadProviderProps<TMeta, TStore>,
	ref: SingleFileUploadProviderRef<TMeta, TStore>,
) => {
	const upload = useSingleFileUpload<TMeta, TStore>(props);

	const contextValues = useMemo(() => upload, [upload]);

	useImperativeHandle(ref, () => contextValues, [contextValues]);

	return (
		<SingleFileUploadContext.Provider value={contextValues as any}>
			{typeof children === 'function' ? children(contextValues) : children}
		</SingleFileUploadContext.Provider>
	);
};

export const SingleFileUploadProviderPrimitive = React.forwardRef<
	SingleFileUploadContextType,
	SingleFileUploadProviderProps
>(SingleFileUploadProviderInner) as <
	TMeta extends UppyFileMeta = UppyFileMeta,
	TStore extends UppyStateStore<TMeta> = UppyStateStore<TMeta>,
>(
	props: SingleFileUploadProviderProps<TMeta, TStore>,
) => JSX.Element;

/**
 * SingleFileUpload context provider
 * @example
 * <SingleFileUploadProvider ref={uploadRef}>
 * 	...
 * </SingleFileUploadProvider>
 * @example
 * <SingleFileUploadProvider ref={uploadRef}>
 * 	{(upload) => (
 * 		...
 * 	)}
 * </SingleFileUploadProvider>
 */
export const SingleFileUploadProvider = observer(
	SingleFileUploadProviderPrimitive,
) as <
	TMeta extends UppyFileMeta = UppyFileMeta,
	TStore extends UppyStateStore<TMeta> = UppyStateStore<TMeta>,
>(
	props: SingleFileUploadProviderProps<TMeta, TStore>,
) => JSX.Element;

export default SingleFileUploadProvider;
